import { Button } from '@kerfjs/ui/button';
import { Toolbar } from '@kerfjs/ui/toolbar';
import { type SafeHtml } from 'kerfjs';

import { workspace } from '../../../data/state.js';
import { isRequester } from '../../../lib/context.js';

/** What the demand footer renders from; read from the open demand unless the catalog supplies it. */
export interface DemandFooterState {
  status: string;
  requester: boolean;
  lineCount: number;
  /** The requester's return questions are still open. */
  needsAnswers: boolean;
  /** Staff have not started on the submitted demand yet (HS2-X22G63). */
  withdrawable: boolean;
}

/** The footer state of the demand open in the editor. */
export function openDemandFooterState(): DemandFooterState {
  const demand = workspace.value?.demand as { status?: string; withdrawable?: boolean } | undefined;
  const status = demand?.status ?? '';
  return {
    status,
    requester: isRequester(),
    lineCount: ((workspace.value?.lineItems as unknown[] | undefined) ?? []).length,
    needsAnswers:
      status === 'returned' &&
      ((workspace.value?.forms ?? []) as { status: string; reason?: string }[]).some(
        (form) => form.status === 'open' && form.reason === 'demand-return',
      ),
    withdrawable: demand?.withdrawable === true,
  };
}

/** Whether the open demand has a footer at all, so the shell mounts `mainFooter` only then. */
export function demandFooterShown(state: DemandFooterState = openDemandFooterState()): boolean {
  const { status, requester, withdrawable } = state;
  return (
    status === 'draft' || (requester && status === 'returned') || (requester && status === 'submitted' && withdrawable)
  );
}

/**
 * Demand footer, private to AppShell and directly reusable by the UX catalog. A draft (or a
 * requester's returned demand) offers Submit / Resubmit Demand: the page's primary action, so a
 * brand Button like SettingsFooter's save (HS2-7CCGDH). A requester's submitted demand that staff have not
 * started on offers a secondary **Withdraw to Draft** (HS2-X22G63). Other states have no footer.
 */
export function DemandFooter({ state = openDemandFooterState() }: { state?: DemandFooterState } = {}): SafeHtml {
  const { status, requester, lineCount, needsAnswers, withdrawable } = state;
  if (!demandFooterShown(state)) return <></>;
  if (status === 'submitted' && requester && withdrawable)
    return (
      <Toolbar
        className="demand-footer"
        label="Demand Actions"
        dividerSides="t"
        trailing={
          <Button size="default" type="button" tone="brand" emphasis="secondary" action="demand-withdraw">
            Withdraw to Draft
          </Button>
        }
      />
    );
  return (
    <Toolbar
      className="demand-footer"
      label="Demand Actions"
      dividerSides="t"
      trailing={
        <Button
          size="default"
          type="button"
          tone="brand"
          emphasis="primary"
          action={status === 'returned' ? 'demand-resubmit' : 'intake-confirm'}
          disabled={lineCount === 0 || needsAnswers ? true : undefined}
        >
          {status === 'returned' ? 'Resubmit Demand' : 'Submit Demand'}
        </Button>
      }
    />
  );
}
